import React, { useEffect, useState, useRef, Children, Fragment, createElement } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckIcon, XIcon, ChevronDownIcon, ChevronUpIcon, ShieldCheckIcon, ZapIcon, LeafIcon, ClockIcon, BookOpenIcon, ShoppingBagIcon } from 'lucide-react';
// ─── Animation Variants ───────────────────────────────────────────────────────
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12
    }
  }
};
const staggerItem = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55,
      ease: 'easeOut'
    }
  }
};
// ─── Scroll Reveal Wrapper ────────────────────────────────────────────────────
function Reveal({
  children,
  className = '',
  delay = 0
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={{
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: 'easeOut',
        delay
      }
    }
  }} className={className} data-id="element-2">
      {children}
    </motion.div>;
}
function RevealList({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={staggerContainer} className={className} data-id="element-3">
      {children}
    </motion.div>;
}
function RevealItem({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <motion.div variants={staggerItem} className={className} data-id="element-4">
      {children}
    </motion.div>;
}
// ─── Botanical SVG Decorations ────────────────────────────────────────────────
function BotanicalLeft() {
  return <svg className="absolute left-0 top-0 w-48 md:w-64 opacity-20 pointer-events-none" viewBox="0 0 200 300" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5">
      <path d="M20 280 Q40 200 80 160 Q120 120 100 60" stroke="#C9A84C" strokeWidth="2" fill="none" data-id="element-6" />
      <path d="M80 160 Q40 140 20 100" stroke="#C9A84C" strokeWidth="1.5" fill="none" data-id="element-7" />
      <path d="M80 160 Q110 150 130 110" stroke="#C9A84C" strokeWidth="1.5" fill="none" data-id="element-8" />
      <ellipse cx="20" cy="100" rx="18" ry="10" fill="#C9A84C" opacity="0.4" transform="rotate(-30 20 100)" data-id="element-9" />
      <ellipse cx="130" cy="110" rx="18" ry="10" fill="#C9A84C" opacity="0.4" transform="rotate(20 130 110)" data-id="element-10" />
      <path d="M100 60 Q70 40 60 10" stroke="#C9A84C" strokeWidth="1.5" fill="none" data-id="element-11" />
      <path d="M100 60 Q130 40 140 10" stroke="#C9A84C" strokeWidth="1.5" fill="none" data-id="element-12" />
      <ellipse cx="60" cy="10" rx="14" ry="8" fill="#C9A84C" opacity="0.35" transform="rotate(-20 60 10)" data-id="element-13" />
      <ellipse cx="140" cy="10" rx="14" ry="8" fill="#C9A84C" opacity="0.35" transform="rotate(20 140 10)" data-id="element-14" />
      <circle cx="20" cy="280" r="4" fill="#C9A84C" opacity="0.5" data-id="element-15" />
      <circle cx="30" cy="265" r="3" fill="#C9A84C" opacity="0.4" data-id="element-16" />
    </svg>;
}
function BotanicalRight() {
  return <svg className="absolute right-0 bottom-0 w-48 md:w-64 opacity-20 pointer-events-none" viewBox="0 0 200 300" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-17">
      <path d="M180 20 Q160 100 120 140 Q80 180 100 240" stroke="#C9A84C" strokeWidth="2" fill="none" data-id="element-18" />
      <path d="M120 140 Q160 160 180 200" stroke="#C9A84C" strokeWidth="1.5" fill="none" data-id="element-19" />
      <path d="M120 140 Q90 150 70 190" stroke="#C9A84C" strokeWidth="1.5" fill="none" data-id="element-20" />
      <ellipse cx="180" cy="200" rx="18" ry="10" fill="#C9A84C" opacity="0.4" transform="rotate(30 180 200)" data-id="element-21" />
      <ellipse cx="70" cy="190" rx="18" ry="10" fill="#C9A84C" opacity="0.4" transform="rotate(-20 70 190)" data-id="element-22" />
      <path d="M100 240 Q130 260 140 290" stroke="#C9A84C" strokeWidth="1.5" fill="none" data-id="element-23" />
      <path d="M100 240 Q70 260 60 290" stroke="#C9A84C" strokeWidth="1.5" fill="none" data-id="element-24" />
      <ellipse cx="140" cy="290" rx="14" ry="8" fill="#C9A84C" opacity="0.35" transform="rotate(20 140 290)" data-id="element-25" />
      <ellipse cx="60" cy="290" rx="14" ry="8" fill="#C9A84C" opacity="0.35" transform="rotate(-20 60 290)" data-id="element-26" />
    </svg>;
}
// ─── FAQ Accordion ────────────────────────────────────────────────────────────
type FaqItem = {
  q: string;
  a: string;
};
function FaqAccordion({
  items
}: {
  items: FaqItem[];
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return <div className="space-y-3" data-id="element-27">
      {items.map((item, i) => <div key={i} className="border border-[#E8D5B0] rounded-xl overflow-hidden" style={{
      backgroundColor: '#FFFDF7'
    }} data-id="element-28">
          <button onClick={() => setOpenIndex(openIndex === i ? null : i)} className="w-full flex items-center justify-between px-6 py-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]" aria-expanded={openIndex === i} data-id="element-29">
            <span className="font-lato font-700 text-[#2C1810] pr-4" style={{
          fontFamily: 'Lato, sans-serif',
          fontWeight: 700
        }} data-id="element-30">
              {item.q}
            </span>
            {openIndex === i ? <ChevronUpIcon className="w-5 h-5 flex-shrink-0" style={{
          color: '#C9A84C'
        }} data-id="element-31" /> : <ChevronDownIcon className="w-5 h-5 flex-shrink-0" style={{
          color: '#C9A84C'
        }} data-id="element-32" />}
          </button>
          <motion.div initial={false} animate={{
        height: openIndex === i ? 'auto' : 0,
        opacity: openIndex === i ? 1 : 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} style={{
        overflow: 'hidden'
      }} data-id="element-33">
            <div className="px-6 pb-5 text-[#5C3D2E] leading-relaxed" style={{
          fontFamily: 'Lato, sans-serif'
        }} data-id="element-34">
              {item.a}
            </div>
          </motion.div>
        </div>)}
    </div>;
}
// ─── Main Landing Page ────────────────────────────────────────────────────────
export function LandingPage() {
  const [legalModal, setLegalModal] = useState<ModalType>(null);
  const [showBonusDetail, setShowBonusDetail] = useState(false);
  // ─── Meta Pixel: Init + PageView ────────────────────────────────────────
  useEffect(() => {
    if (typeof window !== 'undefined' && !(window as any).fbq) {
      const n: any = (window as any).fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!(window as any)._fbq) (window as any)._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      const t = document.createElement('script');
      t.async = true;
      t.src = 'https://connect.facebook.net/en_US/fbevents.js';
      const s = document.getElementsByTagName('script')[0];
      if (s && s.parentNode) {
        s.parentNode.insertBefore(t, s);
      } else {
        document.head.appendChild(t);
      }
    }
    const fbq = (window as any).fbq;
    if (fbq) {
      fbq('init', '1946211246290244');
      fbq('track', 'PageView');
    }
  }, []);
  const faqItems: FaqItem[] = [{
    q: '¿Cómo recibo el producto?',
    a: 'Al instante — recibes un correo con acceso directo al PDF. Puedes leerlo desde tu celular, tablet o computador.'
  }, {
    q: '¿Funciona si tengo más de 50 años?',
    a: 'Sí — el protocolo está diseñado específicamente para mujeres de 40 años en adelante, incluyendo mujeres en menopausia y perimenopausia.'
  }, {
    q: '¿Qué pasa si no me funciona?',
    a: 'Tienes 7 días de garantía total. Si no estás satisfecha, te devolvemos el dinero sin preguntas.'
  }, {
    q: '¿Necesito experiencia previa en nutrición?',
    a: 'No — el protocolo es paso a paso, desde cero. No necesitas saber nada de nutrición.'
  }, {
    q: '¿Cuánto tiempo necesito al día?',
    a: 'Mínimo 8 minutos diarios. El Ritual Abdomen Ligero está diseñado para mujeres ocupadas.'
  }];
  return <div className="w-full min-h-screen" style={{
    backgroundColor: '#FDF8F0',
    color: '#2C1810'
  }} data-id="element-35">
      {/* ── SECTION 1: HERO ─────────────────────────────────────────────────── */}
      <section className="relative w-full overflow-hidden py-16 md:py-24 px-4" style={{
      background: 'linear-gradient(160deg, #FDF8F0 0%, #F5E6C8 50%, #FDF8F0 100%)'
    }} data-id="element-36">
        <BotanicalLeft data-id="element-37" />
        <BotanicalRight data-id="element-38" />

        <div className="relative z-10 max-w-4xl mx-auto text-center" data-id="element-39">
          {/* Badge */}
          <Reveal data-id="element-40">
            <span className="inline-block px-4 py-1.5 rounded-full text-sm font-bold tracking-widest uppercase mb-6" style={{
            backgroundColor: '#1E3A1F',
            color: '#C9A84C',
            fontFamily: 'Lato, sans-serif',
            letterSpacing: '0.12em'
          }} data-id="element-41">
              ✦ Para Mujeres de 40+ ✦
            </span>
          </Reveal>

          {/* Trust Bar */}
          <Reveal delay={0.15} data-id="element-42">
            <div className="inline-flex items-center justify-center gap-4 md:gap-6 px-6 py-3 rounded-full mb-6" style={{
            backgroundColor: '#1E3A1F'
          }} data-id="element-43">
              <span className="text-xs md:text-sm font-bold whitespace-nowrap" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#F5E6C8'
            }} data-id="element-44">
                🔒 Compra 100% segura
              </span>
              <span className="text-xs md:text-sm font-bold whitespace-nowrap" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#F5E6C8'
            }} data-id="element-45">
                ⚡ Acceso inmediato al PDF
              </span>
              <span className="text-xs md:text-sm font-bold whitespace-nowrap" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#F5E6C8'
            }} data-id="element-46">
                ✅ Garantía de 7 días
              </span>
            </div>
          </Reveal>

          {/* H1 */}
          <Reveal delay={0.1} data-id="element-47">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-black leading-tight mb-6" style={{
            fontFamily: 'Playfair Display, serif',
            color: '#1E3A1F',
            fontWeight: 900
          }} data-id="element-48">
              EL PROTOCOLO COMPLETO PARA{' '}
              <span style={{
              color: '#C9A84C'
            }} data-id="element-49">
                DESINFLAMAR TU ABDOMEN
              </span>{' '}
              Y RECUPERAR TU{' '}
              <span style={{
              color: '#C4622D'
            }} data-id="element-50">
                CONFIANZA 40+
              </span>{' '}
              — Con 3 Bonos Exclusivos Incluidos Hoy
            </h1>
          </Reveal>

          {/* H2 */}
          <Reveal delay={0.2} data-id="element-51">
            <p className="text-lg md:text-xl leading-relaxed max-w-2xl mx-auto mb-10" style={{
            fontFamily: 'Lato, sans-serif',
            color: '#5C3D2E'
          }} data-id="element-52">
              La guía paso a paso para mujeres que han probado todo y siguen
              despertando con la ropa apretada debido al estrés y al cortisol.
            </p>
          </Reveal>

          {/* Product Image */}
          <Reveal delay={0.3} data-id="element-53">
            <div className="relative max-w-2xl mx-auto mb-10" data-id="element-54">
              <div className="absolute inset-0 rounded-2xl blur-2xl opacity-30" style={{
              background: 'radial-gradient(ellipse, #C9A84C 0%, transparent 70%)'
            }} data-id="element-55" />
              <img src="https://cdn.magicpatterns.com/uploads/4wxMhis6J1tE7nUjGVSmJN/heylloyd-image-1772370734402.png" alt="Protocolo Vientre Plano 40+ — vista en laptop y móvil" className="relative w-full rounded-2xl" style={{
              boxShadow: '0 24px 64px rgba(30, 58, 31, 0.18), 0 4px 16px rgba(201, 168, 76, 0.2)'
            }} data-id="element-56" />
              <p className="text-center mt-4" style={{
              fontFamily: 'Lato, sans-serif',
              fontSize: '14px',
              color: '#5C3D2E'
            }} data-id="element-57">
                📱 Accede desde tu celular, tablet o computador — al instante
                después de tu compra
              </p>
            </div>
          </Reveal>

          {/* CTA below image */}
          <Reveal delay={0.35} data-id="element-58">
            <div className="flex flex-col items-center gap-3" data-id="element-59">
              <button className="cta-btn px-8 py-5 rounded-2xl text-lg md:text-xl font-black uppercase tracking-wide cursor-pointer border-0" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#1E3A1F',
              boxShadow: '0 8px 32px rgba(201, 168, 76, 0.4)',
              letterSpacing: '0.05em'
            }} aria-label="Comprar el Protocolo Vientre Plano 40+ por $9.990 CLP" onClick={() => document.getElementById('oferta')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-60">
                ¡QUIERO DESINFLAMARME AHORA!
              </button>
              <p className="text-sm" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#5C3D2E'
            }} data-id="element-61">
                Acceso instantáneo · Solo{' '}
                <span style={{
                textDecoration: 'line-through',
                color: '#9A7A6A',
                marginRight: '4px'
              }} data-id="element-62">
                  $43.000
                </span>
                <strong style={{
                color: '#1E3A1F'
              }} data-id="element-63">
                  $9.990 CLP
                </strong>{' '}
                · Pago único de por vida
              </p>
              <div className="flex items-center gap-2 text-xs" style={{
              color: '#7A5C4A',
              fontFamily: 'Lato, sans-serif'
            }} data-id="element-64">
                <ShieldCheckIcon className="w-4 h-4" style={{
                color: '#C9A84C'
              }} data-id="element-65" />
                <span data-id="element-66">Compra 100% segura · Acceso inmediato al PDF</span>
              </div>

              {/* Guarantee badge */}
              <div className="mt-4 w-full max-w-md rounded-xl px-5 py-4 flex items-start gap-3" style={{
              backgroundColor: '#1E3A1F',
              border: '1px solid rgba(201, 168, 76, 0.35)'
            }} data-id="element-67">
                <ShieldCheckIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
                color: '#C9A84C'
              }} data-id="element-68" />
                <p className="text-sm leading-relaxed text-left" style={{
                fontFamily: 'Lato, sans-serif',
                color: '#E8D5B0'
              }} data-id="element-69">
                  <strong style={{
                  color: '#F5E6C8'
                }} data-id="element-70">
                    Garantía total de 7 días
                  </strong>{' '}
                  — Si en 7 días no estás satisfecha, te devolvemos el{' '}
                  <strong style={{
                  color: '#C9A84C'
                }} data-id="element-71">
                    100% de tu dinero
                  </strong>
                  . Sin preguntas, sin complicaciones.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── SECTION 2: OPENING HOOK ─────────────────────────────────────────── */}
      <section className="w-full py-16 md:py-20 px-4" style={{
      backgroundColor: '#F5E6C8'
    }} data-id="element-72">
        <Reveal data-id="element-73">
          <div className="max-w-3xl mx-auto text-center relative" data-id="element-74">
            <div className="text-8xl md:text-9xl leading-none mb-4 select-none" style={{
            fontFamily: 'Playfair Display, serif',
            color: '#C9A84C',
            opacity: 0.5,
            lineHeight: 0.8
          }} aria-hidden="true" data-id="element-75">
              "
            </div>
            <p className="text-xl md:text-2xl leading-relaxed italic" style={{
            fontFamily: 'Playfair Display, serif',
            color: '#2C1810',
            fontStyle: 'italic'
          }} data-id="element-76">
              Son las 7:30 de la mañana. Te paras frente al espejo, te pones tu
              pantalón favorito y, de repente, el golpe de realidad: no cierra.
              O si cierra, te aprieta tanto que te sientes incómoda antes de
              empezar el día.
            </p>
            <p className="text-xl md:text-2xl leading-relaxed italic mt-4" style={{
            fontFamily: 'Playfair Display, serif',
            color: '#2C1810',
            fontStyle: 'italic'
          }} data-id="element-77">
              No es que hayas ganado grasa de la noche a la mañana. Es esa
              inflamación abdominal{' '}
              <strong style={{
              fontStyle: 'normal',
              color: '#C4622D'
            }} data-id="element-78">
                tensa, dura y frustrante
              </strong>{' '}
              que parece aparecer de la nada y no se va con nada.
            </p>
            <div className="text-8xl md:text-9xl leading-none mt-2 select-none" style={{
            fontFamily: 'Playfair Display, serif',
            color: '#C9A84C',
            opacity: 0.5,
            lineHeight: 0.8
          }} aria-hidden="true" data-id="element-79">
              "
            </div>
          </div>
        </Reveal>
      </section>

      {/* ── SECTION 3: THE PROBLEM ──────────────────────────────────────────── */}
      <section className="w-full py-16 md:py-20 px-4" style={{
      backgroundColor: '#FDF8F0'
    }} data-id="element-80">
        <div className="max-w-3xl mx-auto" data-id="element-81">
          <Reveal data-id="element-82">
            <div className="text-center mb-10" data-id="element-83">
              <span className="text-xs font-black tracking-widest uppercase mb-3 block" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#C9A84C',
              letterSpacing: '0.2em'
            }} data-id="element-84">
                El Problema
              </span>
              <div className="section-divider" data-id="element-85" />
              <h2 className="text-2xl md:text-4xl font-bold leading-tight" style={{
              fontFamily: 'Playfair Display, serif',
              color: '#1E3A1F'
            }} data-id="element-86">
                ¿Por qué tu cuerpo se convierte en una{' '}
                <em style={{
                color: '#C4622D'
              }} data-id="element-87">
                  "esponja de cortisol"
                </em>{' '}
                después de los 40?
              </h2>
            </div>
          </Reveal>

          <Reveal delay={0.1} data-id="element-88">
            <p className="text-lg leading-relaxed mb-6" style={{
            fontFamily: 'Lato, sans-serif',
            color: '#5C3D2E'
          }} data-id="element-89">
              A partir de los 40 años, las reglas del juego cambian. Tu cuerpo
              se vuelve una <strong data-id="element-90">"esponja de cortisol"</strong>. El estrés
              del trabajo, la familia y los cambios hormonales envían una señal
              de alerta a tu sistema nervioso.
            </p>
          </Reveal>

          <Reveal delay={0.15} data-id="element-91">
            <div className="rounded-2xl p-6 md:p-8 mb-6 border-l-4" style={{
            backgroundColor: '#F5E6C8',
            borderLeftColor: '#C4622D'
          }} data-id="element-92">
              <p className="text-lg leading-relaxed font-bold" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#2C1810'
            }} data-id="element-93">
                ¿El resultado?
              </p>
              <p className="text-lg leading-relaxed mt-2" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#5C3D2E'
            }} data-id="element-94">
                Tu digestión se detiene, tu cuerpo retiene líquidos y tu abdomen
                se inflama como un mecanismo de protección. Estás lidiando con
                una{' '}
                <strong style={{
                color: '#1E3A1F'
              }} data-id="element-95">
                  respuesta bioquímica
                </strong>
                , no con un problema de falta de voluntad.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── SECTION 4: WHY IT DIDN'T WORK ──────────────────────────────────── */}
      <section className="w-full py-16 md:py-20 px-4" style={{
      backgroundColor: '#1E3A1F'
    }} data-id="element-96">
        <div className="max-w-3xl mx-auto" data-id="element-97">
          <Reveal data-id="element-98">
            <div className="text-center mb-10" data-id="element-99">
              <span className="text-xs font-black tracking-widest uppercase mb-3 block" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#C9A84C',
              letterSpacing: '0.2em'
            }} data-id="element-100">
                Por Qué No Funcionó
              </span>
              <div className="section-divider" data-id="element-101" />
              <h2 className="text-2xl md:text-4xl font-bold leading-tight" style={{
              fontFamily: 'Playfair Display, serif',
              color: '#F5E6C8'
            }} data-id="element-102">
                Todo lo que intentaste tenía una razón para fallar
              </h2>
            </div>
          </Reveal>

          <RevealList className="space-y-4" data-id="element-103">
            <RevealItem data-id="element-104">
              <div className="flex items-start gap-4 rounded-xl p-5" style={{
              backgroundColor: 'rgba(245, 230, 200, 0.08)',
              border: '1px solid rgba(201, 168, 76, 0.2)'
            }} data-id="element-105">
                <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5" style={{
                backgroundColor: 'rgba(196, 98, 45, 0.2)'
              }} data-id="element-106">
                  <XIcon className="w-4 h-4" style={{
                  color: '#E8856A'
                }} data-id="element-107" />
                </div>
                <div data-id="element-108">
                  <p className="font-bold mb-1" style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#F5E6C8'
                }} data-id="element-109">
                    Las dietas de 1,200 calorías
                  </p>
                  <p style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#C8B89A'
                }} data-id="element-110">
                    Disparan tu cortisol, haciendo que tu cuerpo se aferre a la
                    inflamación por miedo a la hambruna.
                  </p>
                </div>
              </div>
            </RevealItem>

            <RevealItem data-id="element-111">
              <div className="flex items-start gap-4 rounded-xl p-5" style={{
              backgroundColor: 'rgba(245, 230, 200, 0.08)',
              border: '1px solid rgba(201, 168, 76, 0.2)'
            }} data-id="element-112">
                <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5" style={{
                backgroundColor: 'rgba(196, 98, 45, 0.2)'
              }} data-id="element-113">
                  <XIcon className="w-4 h-4" style={{
                  color: '#E8856A'
                }} data-id="element-114" />
                </div>
                <div data-id="element-115">
                  <p className="font-bold mb-1" style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#F5E6C8'
                }} data-id="element-116">
                    El cardio intenso de una hora
                  </p>
                  <p style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#C8B89A'
                }} data-id="element-117">
                    Inflama aún más tus tejidos y agota tus glándulas
                    suprarrenales, empeorando el ciclo de cortisol.
                  </p>
                </div>
              </div>
            </RevealItem>

            <RevealItem data-id="element-118">
              <div className="flex items-start gap-4 rounded-xl p-5" style={{
              backgroundColor: 'rgba(245, 230, 200, 0.08)',
              border: '1px solid rgba(201, 168, 76, 0.2)'
            }} data-id="element-119">
                <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5" style={{
                backgroundColor: 'rgba(196, 98, 45, 0.2)'
              }} data-id="element-120">
                  <XIcon className="w-4 h-4" style={{
                  color: '#E8856A'
                }} data-id="element-121" />
                </div>
                <div data-id="element-122">
                  <p className="font-bold mb-1" style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#F5E6C8'
                }} data-id="element-123">
                    Los batidos verdes "detox"
                  </p>
                  <p style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#C8B89A'
                }} data-id="element-124">
                    Suelen estar llenos de fibra cruda que tu sistema digestivo
                    lento no puede procesar, causando más gas y distensión.
                  </p>
                </div>
              </div>
            </RevealItem>

            <RevealItem data-id="element-125">
              <div className="flex items-start gap-4 rounded-xl p-5" style={{
              backgroundColor: 'rgba(245, 230, 200, 0.08)',
              border: '1px solid rgba(201, 168, 76, 0.2)'
            }} data-id="element-126">
                <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5" style={{
                backgroundColor: 'rgba(196, 98, 45, 0.2)'
              }} data-id="element-127">
                  <XIcon className="w-4 h-4" style={{
                  color: '#E8856A'
                }} data-id="element-128" />
                </div>
                <div data-id="element-129">
                  <p className="font-bold mb-1" style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#F5E6C8'
                }} data-id="element-130">
                    Ignorar el descanso
                  </p>
                  <p style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#C8B89A'
                }} data-id="element-131">
                    Hace que tu hormona del hambre (ghrelina) tome el control,
                    saboteando cualquier esfuerzo que hagas durante el día.
                  </p>
                </div>
              </div>
            </RevealItem>
          </RevealList>
        </div>
      </section>

      {/* ── SECTION 5: THE MECHANISM ────────────────────────────────────────── */}
      <section className="w-full py-16 md:py-20 px-4" style={{
      backgroundColor: '#FDF8F0'
    }} data-id="element-132">
        <div className="max-w-3xl mx-auto" data-id="element-133">
          <Reveal data-id="element-134">
            <div className="text-center mb-10" data-id="element-135">
              <span className="text-xs font-black tracking-widest uppercase mb-3 block" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#C9A84C',
              letterSpacing: '0.2em'
            }} data-id="element-136">
                El Mecanismo Único
              </span>
              <div className="section-divider" data-id="element-137" />
              <h2 className="text-2xl md:text-4xl font-bold leading-tight" style={{
              fontFamily: 'Playfair Display, serif',
              color: '#1E3A1F'
            }} data-id="element-138">
                El Método de Regulación HHA
              </h2>
            </div>
          </Reveal>

          <Reveal delay={0.1} data-id="element-139">
            <div className="rounded-2xl p-8 md:p-10 text-center relative overflow-hidden" style={{
            background: 'linear-gradient(135deg, #F5E6C8 0%, #FDF8F0 100%)',
            border: '2px solid #C9A84C'
          }} data-id="element-140">
              <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-10" style={{
              background: '#C9A84C',
              transform: 'translate(30%, -30%)'
            }} data-id="element-141" />
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{
              backgroundColor: '#1E3A1F'
            }} data-id="element-142">
                <ZapIcon className="w-8 h-8" style={{
                color: '#C9A84C'
              }} data-id="element-143" />
              </div>
              <p className="text-lg md:text-xl leading-relaxed" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#2C1810'
            }} data-id="element-144">
                En lugar de atacar tu cuerpo con restricciones, este protocolo
                utiliza{' '}
                <strong style={{
                color: '#1E3A1F'
              }} data-id="element-145">
                  micro-pasos
                </strong>{' '}
                para "apagar" la señal de estrés de tu cerebro.
              </p>
              <p className="text-lg md:text-xl leading-relaxed mt-4" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#5C3D2E'
            }} data-id="element-146">
                Al calmar el eje{' '}
                <strong style={{
                color: '#C4622D'
              }} data-id="element-147">
                  Hipotálamo-Hipofisario-Adrenal
                </strong>
                , tu cuerpo finalmente recibe el permiso para soltar el agua
                retenida y liberar los gases atrapados, devolviéndote la
                ligereza en tiempo récord.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── SECTION 6: WHAT YOU GET ─────────────────────────────────────────── */}
      <section className="w-full py-16 md:py-20 px-4" style={{
      backgroundColor: '#F5E6C8'
    }} data-id="element-148">
        <div className="max-w-4xl mx-auto" data-id="element-149">
          <Reveal data-id="element-150">
            <div className="text-center mb-12" data-id="element-151">
              <span className="text-xs font-black tracking-widest uppercase mb-3 block" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#C4622D',
              letterSpacing: '0.2em'
            }} data-id="element-152">
                Lo Que Recibes
              </span>
              <div className="section-divider" data-id="element-153" />
              <h2 className="text-2xl md:text-4xl font-bold leading-tight" style={{
              fontFamily: 'Playfair Display, serif',
              color: '#1E3A1F'
            }} data-id="element-154">
                Todo lo que incluye tu protocolo
              </h2>
            </div>
          </Reveal>

          <RevealList className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-155">
            <RevealItem data-id="element-156">
              <div className="rounded-2xl p-6 h-full" style={{
              backgroundColor: '#FFFDF7',
              border: '1px solid #E8D5B0',
              boxShadow: '0 4px 20px rgba(30,58,31,0.06)'
            }} data-id="element-157">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{
                backgroundColor: '#1E3A1F'
              }} data-id="element-158">
                  <ZapIcon className="w-6 h-6" style={{
                  color: '#C9A84C'
                }} data-id="element-159" />
                </div>
                <h3 className="text-lg font-bold mb-2" style={{
                fontFamily: 'Playfair Display, serif',
                color: '#1E3A1F'
              }} data-id="element-160">
                  Protocolo "Reset" de 72 horas
                </h3>
                <p style={{
                fontFamily: 'Lato, sans-serif',
                color: '#5C3D2E',
                fontSize: '0.95rem'
              }} data-id="element-161">
                  Para resultados inmediatos y visibles desde los primeros días.
                </p>
              </div>
            </RevealItem>

            <RevealItem data-id="element-162">
              <div className="rounded-2xl p-6 h-full" style={{
              backgroundColor: '#FFFDF7',
              border: '1px solid #E8D5B0',
              boxShadow: '0 4px 20px rgba(30,58,31,0.06)'
            }} data-id="element-163">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{
                backgroundColor: '#1E3A1F'
              }} data-id="element-164">
                  <LeafIcon className="w-6 h-6" style={{
                  color: '#C9A84C'
                }} data-id="element-165" />
                </div>
                <h3 className="text-lg font-bold mb-2" style={{
                fontFamily: 'Playfair Display, serif',
                color: '#1E3A1F'
              }} data-id="element-166">
                  Mapa de Alimentos Anti-inflamatorios
                </h3>
                <p style={{
                fontFamily: 'Lato, sans-serif',
                color: '#5C3D2E',
                fontSize: '0.95rem'
              }} data-id="element-167">
                  Qué comer y qué pausar para desinflamar tu cuerpo desde
                  adentro.
                </p>
              </div>
            </RevealItem>

            <RevealItem data-id="element-168">
              <div className="rounded-2xl p-6 h-full" style={{
              backgroundColor: '#FFFDF7',
              border: '1px solid #E8D5B0',
              boxShadow: '0 4px 20px rgba(30,58,31,0.06)'
            }} data-id="element-169">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{
                backgroundColor: '#1E3A1F'
              }} data-id="element-170">
                  <ClockIcon className="w-6 h-6" style={{
                  color: '#C9A84C'
                }} data-id="element-171" />
                </div>
                <h3 className="text-lg font-bold mb-2" style={{
                fontFamily: 'Playfair Display, serif',
                color: '#1E3A1F'
              }} data-id="element-172">
                  Técnica del Nervio Vago
                </h3>
                <p style={{
                fontFamily: 'Lato, sans-serif',
                color: '#5C3D2E',
                fontSize: '0.95rem'
              }} data-id="element-173">
                  Para bajar el cortisol en solo 2 minutos, en cualquier momento
                  del día.
                </p>
              </div>
            </RevealItem>

            <RevealItem data-id="element-174">
              <div className="rounded-2xl p-6 h-full" style={{
              backgroundColor: '#FFFDF7',
              border: '1px solid #E8D5B0',
              boxShadow: '0 4px 20px rgba(30,58,31,0.06)'
            }} data-id="element-175">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{
                backgroundColor: '#1E3A1F'
              }} data-id="element-176">
                  <BookOpenIcon className="w-6 h-6" style={{
                  color: '#C9A84C'
                }} data-id="element-177" />
                </div>
                <h3 className="text-lg font-bold mb-2" style={{
                fontFamily: 'Playfair Display, serif',
                color: '#1E3A1F'
              }} data-id="element-178">
                  Rutina de Movimiento Inteligente
                </h3>
                <p style={{
                fontFamily: 'Lato, sans-serif',
                color: '#5C3D2E',
                fontSize: '0.95rem'
              }} data-id="element-179">
                  8 minutos de movimiento. Cero sudor, máximo flujo linfático y
                  drenaje.
                </p>
              </div>
            </RevealItem>

            <RevealItem data-id="element-180">
              <div className="rounded-2xl p-6 h-full" style={{
              backgroundColor: '#FFFDF7',
              border: '1px solid #E8D5B0',
              boxShadow: '0 4px 20px rgba(30,58,31,0.06)'
            }} data-id="element-181">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{
                backgroundColor: '#1E3A1F'
              }} data-id="element-182">
                  <ShoppingBagIcon className="w-6 h-6" style={{
                  color: '#C9A84C'
                }} data-id="element-183" />
                </div>
                <h3 className="text-lg font-bold mb-2" style={{
                fontFamily: 'Playfair Display, serif',
                color: '#1E3A1F'
              }} data-id="element-184">
                  Guía de Compras Express
                </h3>
                <p style={{
                fontFamily: 'Lato, sans-serif',
                color: '#5C3D2E',
                fontSize: '0.95rem'
              }} data-id="element-185">
                  Para desinflamar tu despensa con productos simples de
                  cualquier mercado.
                </p>
              </div>
            </RevealItem>

            <RevealItem data-id="element-186">
              <div className="rounded-2xl p-6 h-full flex flex-col justify-center items-center text-center" style={{
              background: 'linear-gradient(135deg, #1E3A1F, #2D5016)',
              border: '2px solid #C9A84C',
              boxShadow: '0 4px 20px rgba(30,58,31,0.15)'
            }} data-id="element-187">
                <p className="text-4xl font-black mb-2" style={{
                fontFamily: 'Playfair Display, serif',
                color: '#C9A84C'
              }} data-id="element-188">
                  $9.990
                </p>
                <p className="text-sm font-bold uppercase tracking-wider mb-1" style={{
                fontFamily: 'Lato, sans-serif',
                color: '#F5E6C8'
              }} data-id="element-189">
                  Pago único
                </p>
                <p className="text-xs" style={{
                fontFamily: 'Lato, sans-serif',
                color: '#C8B89A'
              }} data-id="element-190">
                  Acceso de por vida al PDF completo
                </p>
              </div>
            </RevealItem>
          </RevealList>
        </div>
      </section>

      {/* ── SECTION 6.5: BONUS STACK ────────────────────────────────────────── */}
      <section className="w-full py-16 md:py-24 px-4" style={{
      backgroundColor: '#1E3A1F',
      color: '#FDF8F0'
    }} data-id="element-191">
        <div className="max-w-4xl mx-auto" data-id="element-192">
          {/* Headline */}
          <Reveal data-id="element-193">
            <div className="text-center mb-12" data-id="element-194">
              <h2 className="text-3xl md:text-5xl font-black mb-4 leading-tight" style={{
              fontFamily: 'Playfair Display, serif',
              color: '#F5E6C8'
            }} data-id="element-195">
                Además del Protocolo Principal… Recibes ESTO Sin Costo Adicional
              </h2>
              <p className="text-lg md:text-xl" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#C9A84C'
            }} data-id="element-196">
                (Valorado en $43.000 CLP — Tuyo completamente gratis hoy)
              </p>
            </div>
          </Reveal>

          {/* Bridge */}
          <Reveal delay={0.1} data-id="element-197">
            <div className="max-w-2xl mx-auto mb-16 p-8 rounded-2xl" style={{
            backgroundColor: 'rgba(245, 230, 200, 0.05)',
            border: '1px solid rgba(201, 168, 76, 0.2)'
          }} data-id="element-198">
              <p className="text-base md:text-lg leading-relaxed space-y-4" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#E8D5B0'
            }} data-id="element-199">
                <span className="block mb-4" data-id="element-200">Sé lo que estás pensando:</span>
                <span className="block mb-4 italic" style={{
                color: '#C9A84C'
              }} data-id="element-201">
                  "Ya he comprado cosas antes y nunca las termino."
                </span>
                <span className="block mb-4" data-id="element-202">
                  Por eso no solo te entrego el protocolo. Te entrego todo lo
                  que necesitas para empezar HOY, no "cuando tengas tiempo", no
                  "cuando estés lista", no "el lunes".
                </span>
                <span className="block mb-4 font-bold text-white" data-id="element-203">Hoy.</span>
                <span className="block mb-4" data-id="element-204">
                  Porque el problema no eres tú. El problema es que la mayoría
                  de guías te dan el "qué hacer" pero no te dan el cómo
                  arrancar, qué comer mañana ni qué hacer cuando todo falla.
                </span>
                <span className="block mb-4" data-id="element-205">
                  Eso es exactamente lo que resuelven estos 3 bonos.
                </span>
                <span className="block font-bold text-[#C9A84C]" data-id="element-206">
                  Y son completamente gratis cuando te llevas el protocolo hoy.
                </span>
              </p>
            </div>
          </Reveal>

          {/* Bonuses */}
          <div className="space-y-12 mb-16" data-id="element-207">
            {/* Bonus 1 */}
            <Reveal delay={0.2} data-id="element-208">
              <div className="flex flex-col md:flex-row gap-8 items-center p-8 rounded-3xl" style={{
              backgroundColor: '#FDF8F0',
              color: '#2C1810',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
            }} data-id="element-209">
                <div className="w-full md:w-1/3 flex-shrink-0" data-id="element-210">
                  <img src="https://cdn.magicpatterns.com/uploads/3MovrMhxXK2Pb1HcabThEA/ImagBono1.png" alt="KIT 72H ANTI-INFLAMACIÓN" className="w-full rounded-xl shadow-lg" data-id="element-211" />
                </div>
                <div className="w-full md:w-2/3" data-id="element-212">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E3A1F] text-[#F5E6C8] text-sm font-bold mb-4" data-id="element-213">
                    <ZapIcon className="w-4 h-4 text-[#C9A84C]" data-id="element-214" />
                    BONO #1
                  </div>
                  <h3 className="text-2xl md:text-3xl font-black mb-2" style={{
                  fontFamily: 'Playfair Display, serif'
                }} data-id="element-215">
                    KIT 72H ANTI-INFLAMACIÓN
                  </h3>
                  <p className="text-lg font-bold mb-4" style={{
                  color: '#C4622D'
                }} data-id="element-216">
                    (Arranque Rápido 40+)
                  </p>
                  <p className="text-sm font-bold mb-6 text-[#7A5C4A]" data-id="element-217">
                    Valor real:{' '}
                    <span className="line-through" data-id="element-218">$14.990 CLP</span> —{' '}
                    <span className="text-[#1E3A1F]" data-id="element-219">HOY: GRATIS</span>
                  </p>
                  <div className="space-y-4 text-sm md:text-base mb-6 text-[#5C3D2E]" data-id="element-220">
                    <p data-id="element-221">
                      ¿Sabes cuál es el momento más difícil de cualquier
                      protocolo? El primero. No el día 10. No la semana 3. El
                      primer día. Ese momento en que abres la guía, la miras… y
                      no sabes exactamente por dónde empezar.
                    </p>
                    <p data-id="element-222">
                      Este bono elimina ese problema para siempre. El KIT 72H es
                      tu arranque express: un mini plan de 3 días diseñado para
                      que bajes la hinchazón visible, sientas ligereza y entres
                      al protocolo principal con impulso real.
                    </p>
                    <p data-id="element-223">
                      No es otra dieta. No es un reto extremo. Es un arranque.
                    </p>
                  </div>
                  <ul className="space-y-3 mb-6" data-id="element-224">
                    {['Menú mix & match (elige lo que tengas, sin improvisar)', 'Lista de compras express (lista en 5 min)', 'Rutina AM/PM de solo 10 minutos (mañana + noche)', 'Checklist diario (marca y avanza)', 'Tracker 3 días (para ver tu progreso)', 'Plan de rescate (si te inflamas igual, qué hacer en 6 horas)'].map((item, i) => <li key={i} className="flex items-start gap-3 text-sm" data-id="element-225">
                        <CheckIcon className="w-5 h-5 text-[#C4622D] flex-shrink-0 mt-0.5" data-id="element-226" />
                        <span className="text-[#2C1810] font-bold" data-id="element-227">{item}</span>
                      </li>)}
                  </ul>
                  <div className="bg-[#F5E6C8] p-4 rounded-xl text-sm italic text-[#5C3D2E] border-l-4 border-[#C9A84C]" data-id="element-228">
                    "Regla de oro del KIT 72H: hacerlo suficientemente bien gana
                    siempre sobre hacerlo perfecto."
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Bonus 2 */}
            <Reveal delay={0.3} data-id="element-229">
              <div className="flex flex-col md:flex-row gap-8 items-center p-8 rounded-3xl" style={{
              backgroundColor: '#FDF8F0',
              color: '#2C1810',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
            }} data-id="element-230">
                <div className="w-full md:w-1/3 flex-shrink-0 md:order-2" data-id="element-231">
                  <img src="https://cdn.magicpatterns.com/uploads/h8MfaGgrbjdwp3Zfi4JNAE/ImagBono2.png" alt="MENÚ CALMA CORTISOL" className="w-full rounded-xl shadow-lg" data-id="element-232" />
                </div>
                <div className="w-full md:w-2/3 md:order-1" data-id="element-233">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E3A1F] text-[#F5E6C8] text-sm font-bold mb-4" data-id="element-234">
                    <BookOpenIcon className="w-4 h-4 text-[#C9A84C]" data-id="element-235" />
                    BONO #2
                  </div>
                  <h3 className="text-2xl md:text-3xl font-black mb-2" style={{
                  fontFamily: 'Playfair Display, serif'
                }} data-id="element-236">
                    MENÚ "CALMA CORTISOL"
                  </h3>
                  <p className="text-lg font-bold mb-4" style={{
                  color: '#C4622D'
                }} data-id="element-237">
                    (Plantillas de Comidas + Sustituciones)
                  </p>
                  <p className="text-sm font-bold mb-6 text-[#7A5C4A]" data-id="element-238">
                    Valor real:{' '}
                    <span className="line-through" data-id="element-239">$14.990 CLP</span> —{' '}
                    <span className="text-[#1E3A1F]" data-id="element-240">HOY: GRATIS</span>
                  </p>
                  <div className="space-y-4 text-sm md:text-base mb-6 text-[#5C3D2E]" data-id="element-241">
                    <p data-id="element-242">
                      El mayor error que cometen las mujeres 40+ cuando quieren
                      desinflamarse no es comer "mal". Es no saber qué comer
                      exactamente cuando están cansadas, ocupadas o estresadas.
                    </p>
                    <p data-id="element-243">
                      Y cuando no sabes qué comer, el cuerpo decide solo. Y
                      cuando el cuerpo decide solo bajo estrés… pide exactamente
                      lo que más te infla.
                    </p>
                    <p data-id="element-244">
                      Este bono te da 12 plantillas de platos listos para que
                      nunca más tengas que pensar "¿y ahora qué como?". Cada
                      plantilla sigue una fórmula de 4 bloques (Proteína,
                      Fibra/vegetales, Carbo inteligente, Grasa/extra saciante).
                    </p>
                  </div>
                  <ul className="space-y-3 mb-6" data-id="element-245">
                    {['12 plantillas listas (desayuno, almuerzo, cena, snacks)', 'Tabla de sustituciones por tiempo, presupuesto o lo que tengas en casa', 'Versión "0 cocina" (para días de caos total)', 'Lista anti-antojos nocturnos (10 swaps que funcionan)', 'Kit de emergencia (3 combinaciones para días imposibles)'].map((item, i) => <li key={i} className="flex items-start gap-3 text-sm" data-id="element-246">
                        <CheckIcon className="w-5 h-5 text-[#C4622D] flex-shrink-0 mt-0.5" data-id="element-247" />
                        <span className="text-[#2C1810] font-bold" data-id="element-248">{item}</span>
                      </li>)}
                  </ul>
                  <div className="bg-[#F5E6C8] p-4 rounded-xl text-sm italic text-[#5C3D2E] border-l-4 border-[#C9A84C]" data-id="element-249">
                    "Repite plantillas. Repetir resultados."
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Bonus 3 */}
            <Reveal delay={0.4} data-id="element-250">
              <div className="flex flex-col md:flex-row gap-8 items-center p-8 rounded-3xl" style={{
              backgroundColor: '#FDF8F0',
              color: '#2C1810',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
            }} data-id="element-251">
                <div className="w-full md:w-1/3 flex-shrink-0" data-id="element-252">
                  <img src="https://cdn.magicpatterns.com/uploads/7G89JDQE7CCRqFXNZ2vNKv/ImagBono3.png" alt="RITUAL 8 MIN ABDOMEN LIGERO" className="w-full rounded-xl shadow-lg" data-id="element-253" />
                </div>
                <div className="w-full md:w-2/3" data-id="element-254">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E3A1F] text-[#F5E6C8] text-sm font-bold mb-4" data-id="element-255">
                    <ClockIcon className="w-4 h-4 text-[#C9A84C]" data-id="element-256" />
                    BONO #3
                  </div>
                  <h3 className="text-2xl md:text-3xl font-black mb-2" style={{
                  fontFamily: 'Playfair Display, serif'
                }} data-id="element-257">
                    RITUAL 8 MIN "ABDOMEN LIGERO"
                  </h3>
                  <p className="text-lg font-bold mb-4" style={{
                  color: '#C4622D'
                }} data-id="element-258">
                    (Guía + Tracker 14 días)
                  </p>
                  <p className="text-sm font-bold mb-6 text-[#7A5C4A]" data-id="element-259">
                    Valor real:{' '}
                    <span className="line-through" data-id="element-260">$13.020 CLP</span> —{' '}
                    <span className="text-[#1E3A1F]" data-id="element-261">HOY: GRATIS</span>
                  </p>
                  <div className="space-y-4 text-sm md:text-base mb-6 text-[#5C3D2E]" data-id="element-262">
                    <p data-id="element-263">
                      Hay algo que nadie te dice sobre el abdomen inflamado en
                      los 40+: No siempre es lo que comes. A veces es lo que no
                      sueltas.
                    </p>
                    <p data-id="element-264">
                      La tensión acumulada. El estrés que se guarda en el
                      cuerpo. El cortisol que no baja porque nunca le dices al
                      sistema nervioso que el peligro pasó.
                    </p>
                    <p data-id="element-265">
                      Este ritual de 8 minutos (mañana o noche) es tu "botón de
                      reset" diario. No es yoga. No es meditación larga. Son 8
                      minutos exactos para decirle a tu cuerpo: "Estás a salvo.
                      Ya puedes soltar."
                    </p>
                  </div>
                  <ul className="space-y-3 mb-6" data-id="element-266">
                    {['Ritual minuto a minuto (cronómetro al celular)', 'Versión AM (activar + deshinchar)', 'Versión PM (relajar + dormir mejor)', 'Checklist de 1 página (printable)', 'Tracker 14 días (1 casilla al día)', 'Scripts anti-ansiedad (qué decirte cuando quieres tirar todo)', 'Plan mínimo para días de emergencia (2 min respiración + 2 min rodillas al pecho + 4 min caminata = listo)'].map((item, i) => <li key={i} className="flex items-start gap-3 text-sm" data-id="element-267">
                        <CheckIcon className="w-5 h-5 text-[#C4622D] flex-shrink-0 mt-0.5" data-id="element-268" />
                        <span className="text-[#2C1810] font-bold" data-id="element-269">{item}</span>
                      </li>)}
                  </ul>
                  <div className="bg-[#F5E6C8] p-4 rounded-xl text-sm italic text-[#5C3D2E] border-l-4 border-[#C9A84C]" data-id="element-270">
                    "No necesitas más fuerza de voluntad. Necesitas un ritual
                    corto que te devuelva al centro."
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Value Table */}
          <Reveal delay={0.5} data-id="element-271">
            <div className="max-w-3xl mx-auto rounded-2xl overflow-hidden mb-16" style={{
            backgroundColor: '#FDF8F0',
            border: '2px solid #C9A84C'
          }} data-id="element-272">
              <div className="bg-[#C9A84C] text-[#1E3A1F] font-black text-center py-4 text-lg md:text-xl uppercase tracking-wider" data-id="element-273">
                Resumen del Stack
              </div>
              <div className="p-6 md:p-8" data-id="element-274">
                <div className="overflow-x-auto" data-id="element-275">
                  <table className="w-full text-left border-collapse min-w-[500px]" data-id="element-276">
                    <thead data-id="element-277">
                      <tr className="border-b-2 border-[#E8D5B0]" data-id="element-278">
                        <th className="py-4 px-4 text-[#7A5C4A] font-bold" data-id="element-279">
                          #
                        </th>
                        <th className="py-4 px-4 text-[#7A5C4A] font-bold" data-id="element-280">
                          Bono
                        </th>
                        <th className="py-4 px-4 text-[#7A5C4A] font-bold text-right" data-id="element-281">
                          Valor Real
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-[#2C1810]" data-id="element-282">
                      <tr className="border-b border-[#E8D5B0]" data-id="element-283">
                        <td className="py-4 px-4" data-id="element-284">
                          <ZapIcon className="w-5 h-5 text-[#C4622D]" data-id="element-285" />
                        </td>
                        <td className="py-4 px-4 font-bold" data-id="element-286">
                          KIT 72H Anti-Inflamación
                        </td>
                        <td className="py-4 px-4 text-right line-through text-[#7A5C4A]" data-id="element-287">
                          $14.990 CLP
                        </td>
                      </tr>
                      <tr className="border-b border-[#E8D5B0]" data-id="element-288">
                        <td className="py-4 px-4" data-id="element-289">
                          <BookOpenIcon className="w-5 h-5 text-[#C4622D]" data-id="element-290" />
                        </td>
                        <td className="py-4 px-4 font-bold" data-id="element-291">
                          Menú "Calma Cortisol"
                        </td>
                        <td className="py-4 px-4 text-right line-through text-[#7A5C4A]" data-id="element-292">
                          $14.990 CLP
                        </td>
                      </tr>
                      <tr className="border-b border-[#E8D5B0]" data-id="element-293">
                        <td className="py-4 px-4" data-id="element-294">
                          <ClockIcon className="w-5 h-5 text-[#C4622D]" data-id="element-295" />
                        </td>
                        <td className="py-4 px-4 font-bold" data-id="element-296">
                          Ritual 8 Min "Abdomen Ligero"
                        </td>
                        <td className="py-4 px-4 text-right line-through text-[#7A5C4A]" data-id="element-297">
                          $13.020 CLP
                        </td>
                      </tr>
                      <tr className="bg-[#F5E6C8]" data-id="element-298">
                        <td className="py-6 px-4" data-id="element-299">
                          <ShoppingBagIcon className="w-6 h-6 text-[#1E3A1F]" data-id="element-300" />
                        </td>
                        <td className="py-6 px-4 font-black text-lg text-[#1E3A1F]" data-id="element-301">
                          TOTAL DEL STACK
                        </td>
                        <td className="py-6 px-4 text-right font-black text-lg line-through text-[#7A5C4A]" data-id="element-302">
                          $43.000 CLP
                        </td>
                      </tr>
                      <tr className="bg-[#1E3A1F] text-[#F5E6C8]" data-id="element-303">
                        <td className="py-6 px-4" data-id="element-304">
                          <CheckIcon className="w-6 h-6 text-[#C9A84C]" data-id="element-305" />
                        </td>
                        <td className="py-6 px-4" data-id="element-306">
                          <span className="font-black text-xl text-[#C9A84C] block" data-id="element-307">
                            Tu inversión hoy
                          </span>
                          <span className="text-xs text-[#C8B89A] block mt-1" style={{
                          fontFamily: 'Lato, sans-serif'
                        }} data-id="element-308">
                            Incluye los 3 bonos sin costo adicional
                          </span>
                        </td>
                        <td className="py-6 px-4 text-right font-black text-2xl text-[#C9A84C]" data-id="element-309">
                          $9.990 CLP
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Closing CTA */}
          <Reveal delay={0.6} data-id="element-310">
            <div className="text-center max-w-2xl mx-auto" data-id="element-311">
              <p className="text-lg md:text-xl mb-6 leading-relaxed" style={{
              color: '#E8D5B0'
            }} data-id="element-312">
                Estos 3 bonos no son relleno. Son exactamente lo que necesitas
                para que el protocolo principal funcione en tu vida real. Juntos
                con el protocolo principal, tienes un sistema completo para
                desinflamar, regular y recuperar tu confianza.
              </p>
              <div className="bg-[#C4622D] text-white p-6 rounded-xl mb-10 shadow-lg" style={{
              border: '1px solid rgba(255,255,255,0.2)'
            }} data-id="element-313">
                <h4 className="font-black text-xl mb-2 flex items-center justify-center gap-2" data-id="element-314">
                  <ZapIcon className="w-6 h-6" data-id="element-315" />
                  PERO HAY ALGO IMPORTANTE:
                </h4>
                <p className="text-sm md:text-base" data-id="element-316">
                  Estos bonos están disponibles solo para quienes se lleven el
                  protocolo hoy. No se venden por separado. No hay garantía de
                  que sigan disponibles la próxima vez que entres a esta página.
                </p>
              </div>

              <p className="text-[#C9A84C] font-bold text-xl mb-6" data-id="element-317">
                👇 El siguiente paso es simple:
              </p>

              <button className="cta-btn px-8 py-6 rounded-2xl text-lg md:text-xl font-black uppercase tracking-wide cursor-pointer border-0 w-full" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#1E3A1F',
              boxShadow: '0 8px 32px rgba(201, 168, 76, 0.4)',
              letterSpacing: '0.05em'
            }} aria-label="Comprar el Protocolo Vientre Plano 40+ y obtener 3 bonos gratis" onClick={() => document.getElementById('oferta')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-318">
                SÍ, QUIERO EL PROTOCOLO + LOS 3 BONOS GRATIS
              </button>
              <p className="text-sm mt-4" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#C9A84C'
            }} data-id="element-319">
                (Acceso inmediato — Descarga en segundos)
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── SECTION 7: WHO IT'S FOR / NOT FOR ──────────────────────────────── */}
      <section className="w-full py-16 md:py-20 px-4" style={{
      backgroundColor: '#FDF8F0'
    }} data-id="element-320">
        <div className="max-w-4xl mx-auto" data-id="element-321">
          <Reveal data-id="element-322">
            <div className="text-center mb-12" data-id="element-323">
              <span className="text-xs font-black tracking-widest uppercase mb-3 block" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#C9A84C',
              letterSpacing: '0.2em'
            }} data-id="element-324">
                ¿Es Para Ti?
              </span>
              <div className="section-divider" data-id="element-325" />
              <h2 className="text-2xl md:text-4xl font-bold" style={{
              fontFamily: 'Playfair Display, serif',
              color: '#1E3A1F'
            }} data-id="element-326">
                Este protocolo es para ti si...
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-327">
            {/* FOR */}
            <Reveal delay={0.1} data-id="element-328">
              <div className="rounded-2xl p-6 md:p-8" style={{
              backgroundColor: '#F0F7F0',
              border: '2px solid #2D5016'
            }} data-id="element-329">
                <h3 className="text-xl font-bold mb-5 flex items-center gap-2" style={{
                fontFamily: 'Playfair Display, serif',
                color: '#1E3A1F'
              }} data-id="element-330">
                  <CheckIcon className="w-6 h-6" style={{
                  color: '#2D5016'
                }} data-id="element-331" />
                  Sí es para ti
                </h3>
                <ul className="space-y-3" data-id="element-332">
                  <li className="flex items-start gap-3" data-id="element-333">
                    <CheckIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
                    color: '#2D5016'
                  }} data-id="element-334" />
                    <span style={{
                    fontFamily: 'Lato, sans-serif',
                    color: '#2C1810'
                  }} data-id="element-335">
                      Mujeres de 40+ que sienten que su cuerpo ya no responde a
                      lo que antes funcionaba.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-336">
                    <CheckIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
                    color: '#2D5016'
                  }} data-id="element-337" />
                    <span style={{
                    fontFamily: 'Lato, sans-serif',
                    color: '#2C1810'
                  }} data-id="element-338">
                      Profesionales y madres que viven con altos niveles de
                      estrés diario.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-339">
                    <CheckIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
                    color: '#2D5016'
                  }} data-id="element-340" />
                    <span style={{
                    fontFamily: 'Lato, sans-serif',
                    color: '#2C1810'
                  }} data-id="element-341">
                      Mujeres que sufren de pesadez abdominal y gases después de
                      comer.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-342">
                    <CheckIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
                    color: '#2D5016'
                  }} data-id="element-343" />
                    <span style={{
                    fontFamily: 'Lato, sans-serif',
                    color: '#2C1810'
                  }} data-id="element-344">
                      Aquellas que buscan una solución práctica, rápida y basada
                      en la biología, no en el sufrimiento.
                    </span>
                  </li>
                </ul>
              </div>
            </Reveal>

            {/* NOT FOR */}
            <Reveal delay={0.2} data-id="element-345">
              <div className="rounded-2xl p-6 md:p-8" style={{
              backgroundColor: '#FDF5F2',
              border: '2px solid #E8C5B5'
            }} data-id="element-346">
                <h3 className="text-xl font-bold mb-5 flex items-center gap-2" style={{
                fontFamily: 'Playfair Display, serif',
                color: '#7A3020'
              }} data-id="element-347">
                  <XIcon className="w-6 h-6" style={{
                  color: '#C4622D'
                }} data-id="element-348" />
                  No es para ti si...
                </h3>
                <ul className="space-y-3" data-id="element-349">
                  <li className="flex items-start gap-3" data-id="element-350">
                    <XIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
                    color: '#C4622D'
                  }} data-id="element-351" />
                    <span style={{
                    fontFamily: 'Lato, sans-serif',
                    color: '#5C3D2E'
                  }} data-id="element-352">
                      Buscas una solución de pérdida de peso extrema en días.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-353">
                    <XIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
                    color: '#C4622D'
                  }} data-id="element-354" />
                    <span style={{
                    fontFamily: 'Lato, sans-serif',
                    color: '#5C3D2E'
                  }} data-id="element-355">
                      Eres atleta de alto rendimiento sin problemas de
                      inflamación por estrés.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-356">
                    <XIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
                    color: '#C4622D'
                  }} data-id="element-357" />
                    <span style={{
                    fontFamily: 'Lato, sans-serif',
                    color: '#5C3D2E'
                  }} data-id="element-358">
                      No estás dispuesta a cambiar tu café en ayunas por un
                      protocolo más saludable.
                    </span>
                  </li>
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── SECTION 8: HOW IT WORKS ─────────────────────────────────────────── */}
      <section className="w-full py-16 md:py-20 px-4" style={{
      backgroundColor: '#1E3A1F'
    }} data-id="element-359">
        <div className="max-w-3xl mx-auto" data-id="element-360">
          <Reveal data-id="element-361">
            <div className="text-center mb-12" data-id="element-362">
              <span className="text-xs font-black tracking-widest uppercase mb-3 block" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#C9A84C',
              letterSpacing: '0.2em'
            }} data-id="element-363">
                Cómo Funciona
              </span>
              <div className="section-divider" data-id="element-364" />
              <h2 className="text-2xl md:text-4xl font-bold" style={{
              fontFamily: 'Playfair Display, serif',
              color: '#F5E6C8'
            }} data-id="element-365">
                Tres pasos hacia tu ligereza
              </h2>
            </div>
          </Reveal>

          <RevealList className="space-y-6" data-id="element-366">
            <RevealItem data-id="element-367">
              <div className="flex items-start gap-5" data-id="element-368">
                <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-xl font-black" style={{
                backgroundColor: '#C9A84C',
                color: '#1E3A1F',
                fontFamily: 'Playfair Display, serif'
              }} data-id="element-369">
                  1
                </div>
                <div className="pt-2" data-id="element-370">
                  <h3 className="text-xl font-bold mb-2" style={{
                  fontFamily: 'Playfair Display, serif',
                  color: '#F5E6C8'
                }} data-id="element-371">
                    Descarga e identifica
                  </h3>
                  <p style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#C8B89A'
                }} data-id="element-372">
                    Descargas el protocolo e identificas tus disparadores de
                    estrés personales con la guía incluida.
                  </p>
                </div>
              </div>
            </RevealItem>

            <RevealItem data-id="element-373">
              <div className="flex items-start gap-5" data-id="element-374">
                <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-xl font-black" style={{
                backgroundColor: '#C9A84C',
                color: '#1E3A1F',
                fontFamily: 'Playfair Display, serif'
              }} data-id="element-375">
                  2
                </div>
                <div className="pt-2" data-id="element-376">
                  <h3 className="text-xl font-bold mb-2" style={{
                  fontFamily: 'Playfair Display, serif',
                  color: '#F5E6C8'
                }} data-id="element-377">
                    Aplica la fase "Reset"
                  </h3>
                  <p style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#C8B89A'
                }} data-id="element-378">
                    Aplicas la fase Reset de 72 horas con las comidas y
                    respiraciones sugeridas. Sin complicaciones, sin hambre.
                  </p>
                </div>
              </div>
            </RevealItem>

            <RevealItem data-id="element-379">
              <div className="flex items-start gap-5" data-id="element-380">
                <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-xl font-black" style={{
                backgroundColor: '#C9A84C',
                color: '#1E3A1F',
                fontFamily: 'Playfair Display, serif'
              }} data-id="element-381">
                  3
                </div>
                <div className="pt-2" data-id="element-382">
                  <h3 className="text-xl font-bold mb-2" style={{
                  fontFamily: 'Playfair Display, serif',
                  color: '#F5E6C8'
                }} data-id="element-383">
                    Recupera y mantén
                  </h3>
                  <p style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#C8B89A'
                }} data-id="element-384">
                    Recuperas la comodidad en tu ropa y mantienes el resultado
                    con el escudo anti-rebote incluido en el protocolo.
                  </p>
                </div>
              </div>
            </RevealItem>
          </RevealList>
        </div>
      </section>

      {/* ── SECTION 9: TESTIMONIALS ─────────────────────────────────────────── */}
      <section className="w-full py-16 md:py-20 px-4" style={{
      backgroundColor: '#1E3A1F'
    }} data-id="element-385">
        <div className="max-w-4xl mx-auto" data-id="element-386">
          <Reveal data-id="element-387">
            <h2 className="text-2xl md:text-4xl font-black text-center mb-12" style={{
            fontFamily: 'Playfair Display, serif',
            color: '#F5E6C8'
          }} data-id="element-388">
              Las mujeres que ya lo tienen dicen esto:
            </h2>
          </Reveal>

          <div className="space-y-6" data-id="element-389">
            {/* Testimonial 1 */}
            <Reveal delay={0.1} data-id="element-390">
              <div className="rounded-2xl p-6 md:p-8" style={{
              backgroundColor: 'rgba(253, 248, 240, 0.08)',
              border: '1px solid rgba(201, 168, 76, 0.3)'
            }} data-id="element-391">
                <div className="flex items-center gap-1 mb-3" data-id="element-392">
                  {[...Array(5)].map((_, i) => <span key={i} className="text-xl" style={{
                  color: '#C9A84C'
                }} data-id="element-393">
                      ★
                    </span>)}
                </div>
                <p className="text-base md:text-lg leading-relaxed mb-4" style={{
                fontFamily: 'Lato, sans-serif',
                color: '#E8D5B0'
              }} data-id="element-394">
                  "Llevaba 3 años con el abdomen inflamado sin importar lo que
                  comía. En la primera semana noté la diferencia."
                </p>
                <p className="text-sm font-bold" style={{
                fontFamily: 'Lato, sans-serif',
                color: '#C9A84C'
              }} data-id="element-395">
                  — María G., 44 años — Santiago
                </p>
              </div>
            </Reveal>

            {/* Testimonial 2 */}
            <Reveal delay={0.2} data-id="element-396">
              <div className="rounded-2xl p-6 md:p-8" style={{
              backgroundColor: 'rgba(253, 248, 240, 0.08)',
              border: '1px solid rgba(201, 168, 76, 0.3)'
            }} data-id="element-397">
                <div className="flex items-center gap-1 mb-3" data-id="element-398">
                  {[...Array(5)].map((_, i) => <span key={i} className="text-xl" style={{
                  color: '#C9A84C'
                }} data-id="element-399">
                      ★
                    </span>)}
                </div>
                <p className="text-base md:text-lg leading-relaxed mb-4" style={{
                fontFamily: 'Lato, sans-serif',
                color: '#E8D5B0'
              }} data-id="element-400">
                  "Probé mil dietas y nada funcionaba. Este protocolo me explicó
                  por qué — era el cortisol todo el tiempo."
                </p>
                <p className="text-sm font-bold" style={{
                fontFamily: 'Lato, sans-serif',
                color: '#C9A84C'
              }} data-id="element-401">
                  — Carmen L., 51 años — Valparaíso
                </p>
              </div>
            </Reveal>

            {/* Testimonial 3 */}
            <Reveal delay={0.3} data-id="element-402">
              <div className="rounded-2xl p-6 md:p-8" style={{
              backgroundColor: 'rgba(253, 248, 240, 0.08)',
              border: '1px solid rgba(201, 168, 76, 0.3)'
            }} data-id="element-403">
                <div className="flex items-center gap-1 mb-3" data-id="element-404">
                  {[...Array(5)].map((_, i) => <span key={i} className="text-xl" style={{
                  color: '#C9A84C'
                }} data-id="element-405">
                      ★
                    </span>)}
                </div>
                <p className="text-base md:text-lg leading-relaxed mb-4" style={{
                fontFamily: 'Lato, sans-serif',
                color: '#E8D5B0'
              }} data-id="element-406">
                  "Lo mejor es que no tengo que dejar de comer lo que me gusta.
                  Solo cambié el orden y el momento."
                </p>
                <p className="text-sm font-bold" style={{
                fontFamily: 'Lato, sans-serif',
                color: '#C9A84C'
              }} data-id="element-407">
                  — Patricia M., 47 años — Concepción
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── SECTION 9B: ABOUT CREATOR ───────────────────────────────────────── */}
      <section className="w-full py-16 md:py-20 px-4" style={{
      backgroundColor: '#F5E6C8'
    }} data-id="element-408">
        <div className="max-w-4xl mx-auto" data-id="element-409">
          <Reveal data-id="element-410">
            <h2 className="text-2xl md:text-4xl font-black text-center mb-12" style={{
            fontFamily: 'Playfair Display, serif',
            color: '#1E3A1F'
          }} data-id="element-411">
              ¿Quién creó este protocolo?
            </h2>
          </Reveal>

          <Reveal delay={0.1} data-id="element-412">
            <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12" data-id="element-413">
              {/* Professional Photo */}
              <div className="flex-shrink-0" data-id="element-414">
                <div className="w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden" style={{
                border: '3px solid #C9A84C'
              }} data-id="element-415">
                  <img src="https://cdn.magicpatterns.com/uploads/iFYzKwXcX7x7jxZ88NhoxS/rodrigo-rodrigues-wolf-r-t-yppAybPvASA-unsplash.jpg" alt="Nutricionista especializada en salud hormonal femenina" className="w-full h-full object-cover" data-id="element-416" />
                </div>
              </div>

              {/* Text Content */}
              <div className="flex-1 text-center md:text-left" data-id="element-417">
                <p className="text-base md:text-lg leading-relaxed mb-6" style={{
                fontFamily: 'Lato, sans-serif',
                color: '#5C3D2E'
              }} data-id="element-418">
                  Soy nutricionista especializada en salud hormonal femenina.
                  Después de trabajar con cientos de mujeres mayores de 40 que
                  probaban todo sin resultados, entendí que el problema no era
                  la fuerza de voluntad — era el cortisol. Creé este protocolo
                  para darte la guía exacta que nadie te había dado.
                </p>
                <p className="text-sm font-bold italic" style={{
                fontFamily: 'Playfair Display, serif',
                color: '#C9A84C'
              }} data-id="element-419">
                  — Especialista en bienestar femenino 40+
                </p>
              </div>
            </div>
          </Reveal>

          {/* CTA Button */}
          <Reveal delay={0.2} data-id="element-420">
            <div className="text-center mt-12" data-id="element-421">
              <button className="px-8 py-5 rounded-full font-black text-lg md:text-xl uppercase tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-2xl mb-4" style={{
              backgroundColor: '#C9A84C',
              color: '#1E3A1F',
              fontFamily: 'Lato, sans-serif',
              boxShadow: '0 8px 24px rgba(201, 168, 76, 0.4)'
            }} onClick={() => document.getElementById('oferta')?.scrollIntoView({
              behavior: 'smooth'
            })} aria-label="Comprar el Protocolo Vientre Plano 40+ ahora" data-id="element-422">
                ¡QUIERO DESINFLAMARME AHORA!
              </button>
              <p className="text-xs md:text-sm" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#5C3D2E'
            }} data-id="element-423">
                Acceso instantáneo · Solo{' '}
                <span style={{
                textDecoration: 'line-through',
                opacity: 0.6
              }} data-id="element-424">
                  $43.000
                </span>{' '}
                →{' '}
                <strong style={{
                color: '#C9A84C'
              }} data-id="element-425">
                  $9.990 CLP
                </strong>{' '}
                · Pago único de por vida
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── SECTION 10: FAQ ─────────────────────────────────────────────────── */}
      <section className="w-full py-16 md:py-20 px-4" style={{
      backgroundColor: '#F5E6C8'
    }} data-id="element-426">
        <div className="max-w-2xl mx-auto" data-id="element-427">
          <Reveal data-id="element-428">
            <div className="text-center mb-10" data-id="element-429">
              <span className="text-xs font-black tracking-widest uppercase mb-3 block" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#C4622D',
              letterSpacing: '0.2em'
            }} data-id="element-430">
                Preguntas Frecuentes
              </span>
              <div className="section-divider" data-id="element-431" />
              <h2 className="text-2xl md:text-4xl font-bold" style={{
              fontFamily: 'Playfair Display, serif',
              color: '#1E3A1F'
            }} data-id="element-432">
                Resolvemos tus dudas
              </h2>
            </div>
          </Reveal>
          <Reveal delay={0.1} data-id="element-433">
            <FaqAccordion items={faqItems} data-id="element-434" />
          </Reveal>
        </div>
      </section>

      {/* ── SECTION 11: OFFER + FINAL CTA ───────────────────────────────────── */}
      <section id="oferta" className="w-full py-20 md:py-28 px-4 relative overflow-hidden" style={{
      background: 'linear-gradient(160deg, #1E3A1F 0%, #2D5016 50%, #1E3A1F 100%)'
    }} data-id="element-435">
        {/* Decorative circles */}
        <div className="absolute top-0 left-0 w-96 h-96 rounded-full opacity-10 pointer-events-none" style={{
        background: '#C9A84C',
        transform: 'translate(-40%, -40%)'
      }} data-id="element-436" />
        <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full opacity-10 pointer-events-none" style={{
        background: '#C9A84C',
        transform: 'translate(40%, 40%)'
      }} data-id="element-437" />

        <div className="relative z-10 max-w-2xl mx-auto text-center" data-id="element-438">
          <Reveal data-id="element-439">
            <span className="inline-block px-4 py-1.5 rounded-full text-sm font-bold tracking-widest uppercase mb-6" style={{
            backgroundColor: 'rgba(201, 168, 76, 0.15)',
            color: '#C9A84C',
            fontFamily: 'Lato, sans-serif',
            border: '1px solid rgba(201, 168, 76, 0.4)',
            letterSpacing: '0.12em'
          }} data-id="element-440">
              ✦ Oferta Especial ✦
            </span>
          </Reveal>

          <Reveal delay={0.1} data-id="element-441">
            <h2 className="text-3xl md:text-5xl font-black leading-tight mb-4" style={{
            fontFamily: 'Playfair Display, serif',
            color: '#F5E6C8'
          }} data-id="element-442">
              Recupera tu ligereza hoy mismo
            </h2>
          </Reveal>

          <Reveal delay={0.15} data-id="element-443">
            <p className="text-lg md:text-xl leading-relaxed mb-8" style={{
            fontFamily: 'Lato, sans-serif',
            color: '#C8B89A'
          }} data-id="element-444">
              Obtén el Protocolo Vientre Plano 40+ completo por un pago único de
              solo{' '}
              <strong style={{
              color: '#C9A84C'
            }} data-id="element-445">
                $9.990 CLP
              </strong>
              . Es menos de lo que cuesta una cena fuera, pero con resultados
              que verás en tu espejo cada mañana.
            </p>
          </Reveal>

          {/* Price display */}
          <Reveal delay={0.2} data-id="element-446">
            <div className="flex items-center justify-center gap-4 mb-8" data-id="element-447">
              <div data-id="element-448">
                <div className="flex items-center justify-center gap-3 mb-1" data-id="element-449">
                  <span className="text-3xl line-through opacity-50" style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#C8B89A',
                  textDecoration: 'line-through'
                }} data-id="element-450">
                    $43.000
                  </span>
                  <span className="text-xs font-bold uppercase tracking-widest px-2 py-1 rounded-full" style={{
                  backgroundColor: '#C4622D',
                  color: '#FDF8F0',
                  fontFamily: 'Lato, sans-serif'
                }} data-id="element-451">
                    70% OFF
                  </span>
                </div>
                <p className="text-7xl font-black" style={{
                fontFamily: 'Playfair Display, serif',
                color: '#C9A84C',
                lineHeight: 1
              }} data-id="element-452">
                  $9.990
                </p>
                <p className="text-sm mt-1" style={{
                fontFamily: 'Lato, sans-serif',
                color: '#C8B89A'
              }} data-id="element-453">
                  CLP · Pago único · Acceso de por vida
                </p>
              </div>
            </div>
          </Reveal>

          {/* Bonus stack summary in offer */}
          <Reveal delay={0.22} data-id="element-454">
            <div className="max-w-md mx-auto mb-8 rounded-2xl overflow-hidden" style={{
            backgroundColor: 'rgba(253, 248, 240, 0.08)',
            border: '1px solid rgba(201, 168, 76, 0.3)'
          }} data-id="element-455">
              <div className="px-5 py-3 text-center" style={{
              backgroundColor: 'rgba(201, 168, 76, 0.15)',
              borderBottom: '1px solid rgba(201, 168, 76, 0.2)'
            }} data-id="element-456">
                <p className="text-sm font-black uppercase tracking-widest" style={{
                fontFamily: 'Lato, sans-serif',
                color: '#C9A84C'
              }} data-id="element-457">
                  Incluido sin costo adicional
                </p>
              </div>
              <div className="px-5 py-4 space-y-3" data-id="element-458">
                {[{
                icon: <ZapIcon className="w-5 h-5" style={{
                  color: '#C9A84C'
                }} data-id="element-459" />,
                img: 'https://cdn.magicpatterns.com/uploads/3MovrMhxXK2Pb1HcabThEA/ImagBono1.png',
                title: 'KIT 72H Anti-Inflamación',
                subtitle: 'Arranque Rápido 40+',
                value: '$14.990'
              }, {
                icon: <BookOpenIcon className="w-5 h-5" style={{
                  color: '#C9A84C'
                }} data-id="element-460" />,
                img: 'https://cdn.magicpatterns.com/uploads/h8MfaGgrbjdwp3Zfi4JNAE/ImagBono2.png',
                title: 'Menú "Calma Cortisol"',
                subtitle: 'Plantillas + Sustituciones',
                value: '$14.990'
              }, {
                icon: <ClockIcon className="w-5 h-5" style={{
                  color: '#C9A84C'
                }} data-id="element-461" />,
                img: 'https://cdn.magicpatterns.com/uploads/7G89JDQE7CCRqFXNZ2vNKv/ImagBono3.png',
                title: 'Ritual 8 Min "Abdomen Ligero"',
                subtitle: 'Guía + Tracker 14 días',
                value: '$13.020'
              }].map((bonus, i) => <div key={i} className="flex items-center gap-3 p-3 rounded-xl" style={{
                backgroundColor: 'rgba(253, 248, 240, 0.06)',
                border: '1px solid rgba(201, 168, 76, 0.1)'
              }} data-id="element-462">
                    <img src={bonus.img} alt={bonus.title} className="w-12 h-16 object-cover rounded-lg flex-shrink-0" style={{
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }} data-id="element-463" />
                    <div className="flex-1 min-w-0" data-id="element-464">
                      <p className="text-sm font-bold leading-tight" style={{
                    fontFamily: 'Lato, sans-serif',
                    color: '#F5E6C8'
                  }} data-id="element-465">
                        {bonus.title}
                      </p>
                      <p className="text-xs" style={{
                    fontFamily: 'Lato, sans-serif',
                    color: '#A89070'
                  }} data-id="element-466">
                        {bonus.subtitle}
                      </p>
                    </div>
                    <div className="flex flex-col items-end flex-shrink-0" data-id="element-467">
                      <span className="text-xs line-through" style={{
                    color: '#7A5C4A'
                  }} data-id="element-468">
                        {bonus.value}
                      </span>
                      <span className="text-sm font-black" style={{
                    color: '#C9A84C'
                  }} data-id="element-469">
                        GRATIS
                      </span>
                    </div>
                  </div>)}
              </div>
              <div className="px-5 py-3 flex items-center justify-between" style={{
              backgroundColor: 'rgba(201, 168, 76, 0.1)',
              borderTop: '1px solid rgba(201, 168, 76, 0.2)'
            }} data-id="element-470">
                <p className="text-sm font-bold" style={{
                fontFamily: 'Lato, sans-serif',
                color: '#C8B89A'
              }} data-id="element-471">
                  Valor total en bonos:
                </p>
                <div className="flex items-center gap-2" data-id="element-472">
                  <span className="text-sm line-through" style={{
                  color: '#7A5C4A'
                }} data-id="element-473">
                    $43.000
                  </span>
                  <span className="text-lg font-black" style={{
                  color: '#C9A84C'
                }} data-id="element-474">
                    GRATIS
                  </span>
                </div>
              </div>

              {/* Leer más toggle */}
              <button onClick={() => setShowBonusDetail(!showBonusDetail)} className="w-full py-3 flex items-center justify-center gap-2 cursor-pointer border-0 bg-transparent transition-opacity hover:opacity-80" style={{
              borderTop: '1px solid rgba(201, 168, 76, 0.15)'
            }} data-id="element-475">
                <span className="text-sm font-bold underline underline-offset-2" style={{
                fontFamily: 'Lato, sans-serif',
                color: '#C9A84C'
              }} data-id="element-476">
                  {showBonusDetail ? 'Ver menos' : 'Leer más sobre los bonos'}
                </span>
                <ChevronDownIcon className="w-4 h-4 transition-transform duration-300" style={{
                color: '#C9A84C',
                transform: showBonusDetail ? 'rotate(180deg)' : 'rotate(0deg)'
              }} data-id="element-477" />
              </button>

              {/* Expandable bonus detail */}
              {showBonusDetail && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.4,
              ease: 'easeOut'
            }} className="overflow-hidden" data-id="element-478">
                  <div className="px-5 py-6 space-y-8" data-id="element-479">
                    {/* Bono 1 Detail */}
                    <div className="text-left" data-id="element-480">
                      <div className="flex items-center gap-3 mb-3" data-id="element-481">
                        <img src="https://cdn.magicpatterns.com/uploads/3MovrMhxXK2Pb1HcabThEA/ImagBono1.png" alt="KIT 72H Anti-Inflamación" className="w-14 h-20 object-cover rounded-lg flex-shrink-0" style={{
                      boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                    }} data-id="element-482" />
                        <div data-id="element-483">
                          <p className="text-base font-black" style={{
                        fontFamily: 'Playfair Display, serif',
                        color: '#F5E6C8'
                      }} data-id="element-484">
                            KIT 72H ANTI-INFLAMACIÓN
                          </p>
                          <p className="text-xs" style={{
                        fontFamily: 'Lato, sans-serif',
                        color: '#C4622D'
                      }} data-id="element-485">
                            (Arranque Rápido 40+)
                          </p>
                        </div>
                      </div>
                      <div className="space-y-3 text-sm leading-relaxed" style={{
                    fontFamily: 'Lato, sans-serif',
                    color: '#C8B89A'
                  }} data-id="element-486">
                        <p data-id="element-487">
                          ¿Sabes cuál es el momento más difícil de cualquier
                          protocolo? El primero. Ese momento en que abres la
                          guía, la miras… y no sabes exactamente por dónde
                          empezar.
                        </p>
                        <p data-id="element-488">
                          El KIT 72H es tu arranque express: un mini plan de 3
                          días diseñado para que bajes la hinchazón visible,
                          sientas ligereza y entres al protocolo principal con
                          impulso real.
                        </p>
                      </div>
                      <ul className="mt-3 space-y-2" data-id="element-489">
                        {['Menú mix & match (elige lo que tengas, sin improvisar)', 'Lista de compras express (lista en 5 min)', 'Rutina AM/PM de solo 10 minutos (mañana + noche)', 'Checklist diario (marca y avanza)', 'Tracker 3 días (para ver tu progreso)', 'Plan de rescate (si te inflamas igual, qué hacer en 6 horas)'].map((item, i) => <li key={i} className="flex items-start gap-2 text-xs" style={{
                      color: '#E8D5B0'
                    }} data-id="element-490">
                            <CheckIcon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{
                        color: '#C9A84C'
                      }} data-id="element-491" />
                            <span data-id="element-492">{item}</span>
                          </li>)}
                      </ul>
                      <div className="mt-3 p-3 rounded-lg text-xs italic border-l-2" style={{
                    borderColor: '#C9A84C',
                    backgroundColor: 'rgba(201,168,76,0.08)',
                    color: '#C8B89A'
                  }} data-id="element-493">
                        "Regla de oro del KIT 72H: hacerlo suficientemente bien
                        gana siempre sobre hacerlo perfecto."
                      </div>
                    </div>

                    <div style={{
                  borderTop: '1px solid rgba(201,168,76,0.15)'
                }} data-id="element-494" />

                    {/* Bono 2 Detail */}
                    <div className="text-left" data-id="element-495">
                      <div className="flex items-center gap-3 mb-3" data-id="element-496">
                        <img src="https://cdn.magicpatterns.com/uploads/h8MfaGgrbjdwp3Zfi4JNAE/ImagBono2.png" alt="Menú Calma Cortisol" className="w-14 h-20 object-cover rounded-lg flex-shrink-0" style={{
                      boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                    }} data-id="element-497" />
                        <div data-id="element-498">
                          <p className="text-base font-black" style={{
                        fontFamily: 'Playfair Display, serif',
                        color: '#F5E6C8'
                      }} data-id="element-499">
                            MENÚ "CALMA CORTISOL"
                          </p>
                          <p className="text-xs" style={{
                        fontFamily: 'Lato, sans-serif',
                        color: '#C4622D'
                      }} data-id="element-500">
                            (Plantillas de Comidas + Sustituciones)
                          </p>
                        </div>
                      </div>
                      <div className="space-y-3 text-sm leading-relaxed" style={{
                    fontFamily: 'Lato, sans-serif',
                    color: '#C8B89A'
                  }} data-id="element-501">
                        <p data-id="element-502">
                          El mayor error que cometen las mujeres 40+ cuando
                          quieren desinflamarse no es comer "mal". Es no saber
                          qué comer exactamente cuando están cansadas, ocupadas
                          o estresadas.
                        </p>
                        <p data-id="element-503">
                          Este bono te da 12 plantillas de platos listos para
                          que nunca más tengas que pensar "¿y ahora qué como?"
                        </p>
                      </div>
                      <ul className="mt-3 space-y-2" data-id="element-504">
                        {['12 plantillas listas (desayuno, almuerzo, cena, snacks)', 'Tabla de sustituciones por tiempo, presupuesto o lo que tengas en casa', 'Versión "0 cocina" (para días de caos total)', 'Lista anti-antojos nocturnos (10 swaps que funcionan)', 'Kit de emergencia (3 combinaciones para días imposibles)'].map((item, i) => <li key={i} className="flex items-start gap-2 text-xs" style={{
                      color: '#E8D5B0'
                    }} data-id="element-505">
                            <CheckIcon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{
                        color: '#C9A84C'
                      }} data-id="element-506" />
                            <span data-id="element-507">{item}</span>
                          </li>)}
                      </ul>
                      <div className="mt-3 p-3 rounded-lg text-xs italic border-l-2" style={{
                    borderColor: '#C9A84C',
                    backgroundColor: 'rgba(201,168,76,0.08)',
                    color: '#C8B89A'
                  }} data-id="element-508">
                        "Repite plantillas. Repetir resultados."
                      </div>
                    </div>

                    <div style={{
                  borderTop: '1px solid rgba(201,168,76,0.15)'
                }} data-id="element-509" />

                    {/* Bono 3 Detail */}
                    <div className="text-left" data-id="element-510">
                      <div className="flex items-center gap-3 mb-3" data-id="element-511">
                        <img src="https://cdn.magicpatterns.com/uploads/7G89JDQE7CCRqFXNZ2vNKv/ImagBono3.png" alt="Ritual 8 Min Abdomen Ligero" className="w-14 h-20 object-cover rounded-lg flex-shrink-0" style={{
                      boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                    }} data-id="element-512" />
                        <div data-id="element-513">
                          <p className="text-base font-black" style={{
                        fontFamily: 'Playfair Display, serif',
                        color: '#F5E6C8'
                      }} data-id="element-514">
                            RITUAL 8 MIN "ABDOMEN LIGERO"
                          </p>
                          <p className="text-xs" style={{
                        fontFamily: 'Lato, sans-serif',
                        color: '#C4622D'
                      }} data-id="element-515">
                            (Guía + Tracker 14 días)
                          </p>
                        </div>
                      </div>
                      <div className="space-y-3 text-sm leading-relaxed" style={{
                    fontFamily: 'Lato, sans-serif',
                    color: '#C8B89A'
                  }} data-id="element-516">
                        <p data-id="element-517">
                          Hay algo que nadie te dice sobre el abdomen inflamado
                          en los 40+: No siempre es lo que comes. A veces es lo
                          que no sueltas.
                        </p>
                        <p data-id="element-518">
                          Este ritual de 8 minutos (mañana o noche) es tu "botón
                          de reset" diario. Son 8 minutos exactos para decirle a
                          tu cuerpo: "Estás a salvo. Ya puedes soltar."
                        </p>
                      </div>
                      <ul className="mt-3 space-y-2" data-id="element-519">
                        {['Ritual minuto a minuto (cronómetro al celular)', 'Versión AM (activar + deshinchar)', 'Versión PM (relajar + dormir mejor)', 'Checklist de 1 página (printable)', 'Tracker 14 días (1 casilla al día)', 'Scripts anti-ansiedad (qué decirte cuando quieres tirar todo)', 'Plan mínimo para días de emergencia (2 min respiración + 2 min rodillas al pecho + 4 min caminata = listo)'].map((item, i) => <li key={i} className="flex items-start gap-2 text-xs" style={{
                      color: '#E8D5B0'
                    }} data-id="element-520">
                            <CheckIcon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{
                        color: '#C9A84C'
                      }} data-id="element-521" />
                            <span data-id="element-522">{item}</span>
                          </li>)}
                      </ul>
                      <div className="mt-3 p-3 rounded-lg text-xs italic border-l-2" style={{
                    borderColor: '#C9A84C',
                    backgroundColor: 'rgba(201,168,76,0.08)',
                    color: '#C8B89A'
                  }} data-id="element-523">
                        "No necesitas más fuerza de voluntad. Necesitas un
                        ritual corto que te devuelva al centro."
                      </div>
                    </div>
                  </div>
                </motion.div>}
            </div>
          </Reveal>

          <Reveal delay={0.25} data-id="element-524">
            <div className="flex flex-col items-center gap-4" data-id="element-525">
              <button className="cta-btn px-10 py-5 rounded-2xl text-lg md:text-xl font-black uppercase tracking-wide cursor-pointer border-0 w-full max-w-md" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#1E3A1F',
              letterSpacing: '0.04em'
            }} aria-label="Comprar el Protocolo Vientre Plano 40+ ahora por $9.990 CLP" onClick={() => {
              if ((window as any).fbq) {
                ;
                (window as any).fbq('track', 'AddToCart', {
                  content_name: 'Protocolo Vientre Plano 40+',
                  content_type: 'product',
                  value: 6.39,
                  currency: 'USD'
                });
              }
              window.open('https://thesolutionkit.com//cart/50186440966501:1', '_blank');
            }} data-id="element-526">
                ¡QUIERO DESINFLAMARME AHORA!
              </button>

              <p className="text-sm" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#A89070'
            }} data-id="element-527">
                Acceso instantáneo tras la compra
              </p>

              <div className="flex flex-wrap justify-center gap-4 mt-2" data-id="element-528">
                <div className="flex items-center gap-2" data-id="element-529">
                  <ShieldCheckIcon className="w-4 h-4" style={{
                  color: '#C9A84C'
                }} data-id="element-530" />
                  <span className="text-xs" style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#A89070'
                }} data-id="element-531">
                    Compra segura
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-532">
                  <ZapIcon className="w-4 h-4" style={{
                  color: '#C9A84C'
                }} data-id="element-533" />
                  <span className="text-xs" style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#A89070'
                }} data-id="element-534">
                    Acceso inmediato al PDF
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-535">
                  <BookOpenIcon className="w-4 h-4" style={{
                  color: '#C9A84C'
                }} data-id="element-536" />
                  <span className="text-xs" style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#A89070'
                }} data-id="element-537">
                    Acceso de por vida
                  </span>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── SECTION 12: GUARANTEE ───────────────────────────────────────────── */}
      <section className="w-full py-12 md:py-16 px-4" style={{
      backgroundColor: '#1E3A1F'
    }} data-id="element-538">
        <Reveal data-id="element-539">
          <div className="max-w-2xl mx-auto rounded-2xl p-8 md:p-10 text-center relative overflow-hidden" style={{
          background: 'linear-gradient(135deg, #2D5016 0%, #1E3A1F 100%)',
          border: '2px solid rgba(201, 168, 76, 0.4)',
          boxShadow: '0 8px 40px rgba(0,0,0,0.2)'
        }} data-id="element-540">
            <div className="absolute top-0 left-0 w-40 h-40 rounded-full opacity-10 pointer-events-none" style={{
            background: '#C9A84C',
            transform: 'translate(-30%, -30%)'
          }} data-id="element-541" />
            <div className="absolute bottom-0 right-0 w-32 h-32 rounded-full opacity-10 pointer-events-none" style={{
            background: '#C9A84C',
            transform: 'translate(30%, 30%)'
          }} data-id="element-542" />
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{
            backgroundColor: 'rgba(201, 168, 76, 0.15)',
            border: '2px solid rgba(201, 168, 76, 0.3)'
          }} data-id="element-543">
              <ShieldCheckIcon className="w-8 h-8" style={{
              color: '#C9A84C'
            }} data-id="element-544" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold mb-4" style={{
            fontFamily: 'Playfair Display, serif',
            color: '#F5E6C8'
          }} data-id="element-545">
              Garantía total de 7 días
            </h3>
            <p className="text-base md:text-lg leading-relaxed" style={{
            fontFamily: 'Lato, sans-serif',
            color: '#E8D5B0'
          }} data-id="element-546">
              Si en 7 días no estás satisfecha, te devolvemos el{' '}
              <strong style={{
              color: '#C9A84C'
            }} data-id="element-547">
                100% de tu dinero
              </strong>
              . Sin preguntas, sin complicaciones.
            </p>
          </div>
        </Reveal>
      </section>

      {/* ── FOOTER ──────────────────────────────────────────────────────────── */}
      <footer className="w-full py-8 px-4 text-center" style={{
      backgroundColor: '#150F0A',
      borderTop: '1px solid #2C1810'
    }} data-id="element-548">
        <p className="text-sm" style={{
        fontFamily: 'Lato, sans-serif',
        color: '#7A5C4A'
      }} data-id="element-549">
          © 2025 Protocolo Vientre Plano 40+. Todos los derechos reservados.
        </p>
        <div className="flex items-center justify-center gap-4 mt-3" data-id="element-550">
          <button onClick={() => setLegalModal('privacy')} className="text-xs underline underline-offset-2 cursor-pointer border-0 bg-transparent transition-colors hover:opacity-80" style={{
          fontFamily: 'Lato, sans-serif',
          color: '#C9A84C'
        }} data-id="element-551">
            Política de Privacidad
          </button>
          <span style={{
          color: '#3A2010'
        }} data-id="element-552">
            ·
          </span>
          <button onClick={() => setLegalModal('terms')} className="text-xs underline underline-offset-2 cursor-pointer border-0 bg-transparent transition-colors hover:opacity-80" style={{
          fontFamily: 'Lato, sans-serif',
          color: '#C9A84C'
        }} data-id="element-553">
            Términos y Condiciones
          </button>
        </div>
        <p className="text-xs mt-3 max-w-xl mx-auto" style={{
        fontFamily: 'Lato, sans-serif',
        color: '#5C3D2E'
      }} data-id="element-554">
          Este producto es de carácter informativo y no reemplaza el consejo
          médico profesional. Consulta a tu médico antes de iniciar cualquier
          protocolo de salud.
        </p>
      </footer>

      {/* ── LEGAL MODALS ────────────────────────────────────────────────────── */}
      {legalModal && <LegalModal type={legalModal} onClose={() => setLegalModal(null)} data-id="element-555" />}
    </div>;
}
// ─── Legal Modal ──────────────────────────────────────────────────────────────
type ModalType = 'privacy' | 'terms' | null;
function LegalModal({
  type,
  onClose
}: {
  type: ModalType;
  onClose: () => void;
}) {
  if (!type) return null;
  const isPrivacy = type === 'privacy';
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{
    backgroundColor: 'rgba(30, 20, 10, 0.7)',
    backdropFilter: 'blur(4px)'
  }} onClick={onClose} data-id="element-556">
      <motion.div initial={{
      opacity: 0,
      y: 32,
      scale: 0.97
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 16,
      scale: 0.97
    }} transition={{
      duration: 0.3,
      ease: 'easeOut'
    }} className="relative w-full max-w-2xl max-h-[80vh] overflow-y-auto rounded-2xl p-8 md:p-10" style={{
      backgroundColor: '#FDF8F0',
      boxShadow: '0 32px 80px rgba(30,20,10,0.35)'
    }} onClick={e => e.stopPropagation()} data-id="element-557">
        {/* Close button */}
        <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center cursor-pointer border-0 transition-colors" style={{
        backgroundColor: '#F5E6C8',
        color: '#2C1810'
      }} aria-label="Cerrar" data-id="element-558">
          <XIcon className="w-4 h-4" data-id="element-559" />
        </button>

        {/* Header */}
        <div className="mb-6 pb-4" style={{
        borderBottom: '2px solid #F5E6C8'
      }} data-id="element-560">
          <span className="text-xs font-black tracking-widest uppercase block mb-2" style={{
          fontFamily: 'Lato, sans-serif',
          color: '#C9A84C',
          letterSpacing: '0.2em'
        }} data-id="element-561">
            {isPrivacy ? 'Legal' : 'Legal'}
          </span>
          <h2 className="text-2xl md:text-3xl font-bold" style={{
          fontFamily: 'Playfair Display, serif',
          color: '#1E3A1F'
        }} data-id="element-562">
            {isPrivacy ? 'Política de Privacidad' : 'Términos y Condiciones'}
          </h2>
        </div>

        {/* Content */}
        <div className="space-y-5 text-sm leading-relaxed" style={{
        fontFamily: 'Lato, sans-serif',
        color: '#5C3D2E'
      }} data-id="element-563">
          {isPrivacy ? <>
              <p data-id="element-564">
                <strong style={{
              color: '#1E3A1F'
            }} data-id="element-565">
                  Última actualización:
                </strong>{' '}
                Marzo 2025
              </p>
              <div data-id="element-566">
                <h3 className="font-bold text-base mb-2" style={{
              color: '#1E3A1F',
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-567">
                  1. Información que recopilamos
                </h3>
                <p data-id="element-568">
                  Al realizar tu compra, recopilamos únicamente la información
                  necesaria para procesar tu pedido y entregarte el producto
                  digital: nombre y correo electrónico. No almacenamos datos de
                  tarjetas de crédito ni información bancaria.
                </p>
              </div>
              <div data-id="element-569">
                <h3 className="font-bold text-base mb-2" style={{
              color: '#1E3A1F',
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-570">
                  2. Uso de tu información
                </h3>
                <p data-id="element-571">
                  Tu información se utiliza exclusivamente para: (a) entregar el
                  producto digital adquirido, (b) enviarte comunicaciones
                  relacionadas con tu compra, y (c) mejorar nuestros productos y
                  servicios. No vendemos, alquilamos ni compartimos tu
                  información personal con terceros.
                </p>
              </div>
              <div data-id="element-572">
                <h3 className="font-bold text-base mb-2" style={{
              color: '#1E3A1F',
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-573">
                  3. Cookies
                </h3>
                <p data-id="element-574">
                  Esta página puede utilizar cookies técnicas necesarias para su
                  funcionamiento. No utilizamos cookies de seguimiento
                  publicitario sin tu consentimiento.
                </p>
              </div>
              <div data-id="element-575">
                <h3 className="font-bold text-base mb-2" style={{
              color: '#1E3A1F',
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-576">
                  4. Seguridad
                </h3>
                <p data-id="element-577">
                  Implementamos medidas de seguridad estándar de la industria
                  para proteger tu información personal. Todas las transacciones
                  se procesan a través de plataformas de pago seguras y
                  certificadas.
                </p>
              </div>
              <div data-id="element-578">
                <h3 className="font-bold text-base mb-2" style={{
              color: '#1E3A1F',
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-579">
                  5. Tus derechos
                </h3>
                <p data-id="element-580">
                  Tienes derecho a acceder, rectificar o eliminar tus datos
                  personales en cualquier momento. Para ejercer estos derechos,
                  puedes solicitarlo a través de los canales de atención al
                  cliente disponibles en tu plataforma de compra.
                </p>
              </div>
              <div data-id="element-581">
                <h3 className="font-bold text-base mb-2" style={{
              color: '#1E3A1F',
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-582">
                  6. Cambios a esta política
                </h3>
                <p data-id="element-583">
                  Nos reservamos el derecho de actualizar esta política de
                  privacidad en cualquier momento. Los cambios serán publicados
                  en esta página con la fecha de actualización correspondiente.
                </p>
              </div>
            </> : <>
              <p data-id="element-584">
                <strong style={{
              color: '#1E3A1F'
            }} data-id="element-585">
                  Última actualización:
                </strong>{' '}
                Marzo 2025
              </p>
              <div data-id="element-586">
                <h3 className="font-bold text-base mb-2" style={{
              color: '#1E3A1F',
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-587">
                  1. Aceptación de los términos
                </h3>
                <p data-id="element-588">
                  Al adquirir el Protocolo Vientre Plano 40+, aceptas estos
                  Términos y Condiciones en su totalidad. Si no estás de acuerdo
                  con alguno de ellos, te pedimos que no realices la compra.
                </p>
              </div>
              <div data-id="element-589">
                <h3 className="font-bold text-base mb-2" style={{
              color: '#1E3A1F',
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-590">
                  2. Naturaleza del producto
                </h3>
                <p data-id="element-591">
                  El Protocolo Vientre Plano 40+ es un producto digital de
                  carácter informativo y educativo. No constituye consejo
                  médico, diagnóstico ni tratamiento. Siempre consulta a un
                  profesional de la salud antes de iniciar cualquier cambio en
                  tu alimentación o rutina.
                </p>
              </div>
              <div data-id="element-592">
                <h3 className="font-bold text-base mb-2" style={{
              color: '#1E3A1F',
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-593">
                  3. Entrega del producto
                </h3>
                <p data-id="element-594">
                  Tras confirmar tu pago, recibirás acceso inmediato al PDF
                  digital. El acceso es de por vida y para uso personal
                  exclusivo. Queda prohibida la reproducción, distribución o
                  reventa del contenido.
                </p>
              </div>
              <div data-id="element-595">
                <h3 className="font-bold text-base mb-2" style={{
              color: '#1E3A1F',
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-596">
                  4. Política de reembolso
                </h3>
                <p data-id="element-597">
                  Dado que se trata de un producto digital con acceso inmediato,
                  no se ofrecen reembolsos una vez que el producto ha sido
                  descargado o accedido. Si tienes algún problema técnico con tu
                  compra, comunícate a través de la plataforma de pago
                  utilizada.
                </p>
              </div>
              <div data-id="element-598">
                <h3 className="font-bold text-base mb-2" style={{
              color: '#1E3A1F',
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-599">
                  5. Resultados
                </h3>
                <p data-id="element-600">
                  Los resultados mencionados en esta página son representativos
                  y pueden variar según cada persona. No garantizamos resultados
                  específicos. El éxito del protocolo depende de la constancia y
                  las condiciones individuales de cada usuaria.
                </p>
              </div>
              <div data-id="element-601">
                <h3 className="font-bold text-base mb-2" style={{
              color: '#1E3A1F',
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-602">
                  6. Propiedad intelectual
                </h3>
                <p data-id="element-603">
                  Todo el contenido del protocolo, incluyendo textos, gráficos y
                  metodología, es propiedad exclusiva de sus autores y está
                  protegido por las leyes de propiedad intelectual aplicables.
                </p>
              </div>
              <div data-id="element-604">
                <h3 className="font-bold text-base mb-2" style={{
              color: '#1E3A1F',
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-605">
                  7. Ley aplicable
                </h3>
                <p data-id="element-606">
                  Estos términos se rigen por las leyes aplicables en la
                  jurisdicción del vendedor. Cualquier disputa será resuelta de
                  buena fe entre las partes.
                </p>
              </div>
            </>}
        </div>
      </motion.div>
    </motion.div>;
}